import { useEffect, useRef, useState, ReactNode } from "react";

interface DeferredSectionProps {
  children: ReactNode;
  fallback?: ReactNode;
  rootMargin?: string;
}

/**
 * Component that defers rendering children until the section is about to enter viewport
 * This reduces initial JavaScript execution and improves TBT
 */
export const DeferredSection: React.FC<DeferredSectionProps> = ({
  children,
  fallback = null,
  rootMargin = "200px",
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (shouldRender) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldRender(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [shouldRender, rootMargin]);

  return (
    <div ref={ref}>
      {shouldRender ? children : fallback}
    </div>
  );
};

